import { ACTION_ICONS } from "../../config/siteConfig";
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { Modal } from 'react-bootstrap';
import DetailView from '../location/DetailView';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useLoadingBar } from 'react-top-loading-bar';
import ContentLoader from '../loader/ContentLoader';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { airportsService } from '../../services/airports.service';
import { countriesService } from '../../services/countries.service';
import { citiesService } from '../../services/cities.service';

const MySwal = withReactContent(Swal);

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/* ---------- styles ---------- */
const labelStyle = { fontWeight: 600, fontSize: 12.5, color: "#334155", marginBottom: 5 };
const inputStyle = { borderRadius: 6, border: "1px solid #c3cad6", fontSize: 12, height: 34, background: "#fff" };
const filterInputStyle = { ...inputStyle, background: "#f8f9fe", border: "1px solid #e8edf5", borderRadius: 8 };
const STATUS_FILTER_OPTIONS = [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }];
const selectStyles = {
  control: (base) => ({
    ...base, borderRadius: 6, border: "1px solid #c3cad6", minHeight: 34, height: 34,
    fontSize: 12, boxShadow: "none", "&:hover": { borderColor: "var(--btn-bg)" }
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 12 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};
const pageBtn = (active, disabled) => ({
  minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
  border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
  background: active ? "var(--page-active-bg)" : "#fff",
  color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
  fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
});
const chip = (color, bg, border) => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
  borderRadius: 10, background: bg, border: "1px solid " + border, color, fontSize: 13
});
const iconBtn = (bg, color) => ({
  width: 32, height: 32, borderRadius: 8, background: bg, color, border: 'none', padding: 0
});

const initialForm = { airport_name: "", airport_code: "", iata_id: "", icao_id: "", world_area_code: "", country_id: "", country_name: "", country_code: "", city_id: "", city_name: "", city_code: "", latitude: "", longitude: "", status: "active" };
const initialFilters = { search: "", status: "" };

const AirportList = () => {
  const { start, complete } = useLoadingBar();

  /* ---------- list state ---------- */
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  /* ---------- popup (add / edit / view) state ---------- */
  const [modal, setModal] = useState({ show: false, mode: "add", id: null });
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const totalRecords = pagination?.total || 0;
  const totalPage = pagination?.totalPages || 1;
  const activeCount = items.filter(i => i?.status === "active").length;
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);
  const isView = modal.mode === "view";
  const isEdit = modal.mode === "edit";

  useEffect(() => {
    loadItems();
  }, [pagingLimit, pageValue, appliedFilters]);

  /* ---------- API: dropdown sources (active only) ---------- */
  const loadCountryOptions = async () => {
    try {
      const res = await countriesService.list({ page: 1, status: "active" });
      if (!res?.success) { toast.error(res?.message); return []; }
      const options = (res?.resData?.data || []).map(c => ({ value: c.id, label: c.name, code: c.code }));
      setCountryOptions(options);
      return options;
    } catch (err) {
      setCountryOptions([]);
      return [];
    }
  };

  const loadCityOptions = async (countryId) => {
    if (!countryId) { setCityOptions([]); return []; }
    try {
      const res = await citiesService.list({ page: 1, status: "active", country_id: Number(countryId) });
      if (!res?.success) { toast.error(res?.message); return []; }
      const options = (res?.resData?.data || []).map(c => ({ value: c.id, label: c.name, code: c.code }));
      setCityOptions(options);
      return options;
    } catch (err) {
      setCityOptions([]);
      return [];
    }
  };

  useEffect(() => {
    loadCountryOptions();
  }, []);

  /* ---------- API: list ---------- */
  const loadItems = async () => {
    try {
      setLoading(true);
      start();
      const query = { page: Number(pageValue), limit: Number(pagingLimit) };
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null) query[key] = value;
      });
      const res = await airportsService.list(query);
      if (res?.success) {
        const rd = res.resData;
        let rows = Array.isArray(rd) ? rd : rd?.data || [];
        let pg = (Array.isArray(rd) ? res.pagination : rd?.pagination) || null;
        if (!pg) {
          // API returned the full list without pagination info: paginate on the client
          const limit = Number(pagingLimit);
          pg = { total: rows.length, totalPages: Math.max(1, Math.ceil(rows.length / limit)) };
          rows = rows.slice((Number(pageValue) - 1) * limit, Number(pageValue) * limit);
        }
        setItems(rows);
        setPagination(pg);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
      complete();
    }
  };

  /* ---------- filters ---------- */
  const handleFilterText = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  /* ---------- pagination ---------- */
  const handlePagingLimit = (e) => { setPageValue(1); setPagingLimit(e.target.value); };
  const handlePagination = (n) => setPageValue(n);
  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) setPageValue(n);
    setGoPage("");
  };
  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  /* ---------- row <-> form <-> payload ---------- */
  const rowToForm = (row) => ({ airport_name: row?.airport_name ?? "", airport_code: row?.airport_code ?? "", iata_id: row?.iata_id ?? "", icao_id: row?.icao_id ?? "", world_area_code: row?.world_area_code ?? "", country_id: "", country_name: row?.country_name ?? "", country_code: row?.country_code ?? "", city_id: "", city_name: row?.city_name ?? "", city_code: row?.city_code ?? "", latitude: row?.latitude ?? "", longitude: row?.longitude ?? "", status: row?.status || "active" });

  const formToPayload = (f) => ({ airport_name: String(f.airport_name ?? "").trim(), airport_code: String(f.airport_code ?? "").trim(), iata_id: String(f.iata_id ?? "").trim(), icao_id: String(f.icao_id ?? "").trim(), world_area_code: String(f.world_area_code ?? "").trim(), city_name: String(f.city_name ?? "").trim(), city_code: String(f.city_code ?? "").trim(), state: null, country_name: String(f.country_name ?? "").trim(), country_code: String(f.country_code ?? "").trim(), latitude: f.latitude === "" ? 0 : Number(f.latitude), longitude: f.longitude === "" ? 0 : Number(f.longitude), status: f.status });

  /* ---------- popup open / close ---------- */
  const closeModal = () => setModal({ ...modal, show: false });

  const openAdd = () => {
    setForm(initialForm);
    setCityOptions([]);
    setModal({ show: true, mode: "add", id: null });
  };

  const openExisting = async (row, mode) => {
    setModal({ show: true, mode, id: row?.id });
    setForm(initialForm);
    try {
      setModalLoading(true);
      const res = await airportsService.details(row?.id);
      if (!res?.success) {
        toast.error(res?.message);
        setModal(m => ({ ...m, show: false }));
        return;
      }
      const loaded = rowToForm(res.resData?.data ?? res.resData);
      // resolve the stored country/city back to dropdown ids
      const countries = countryOptions.length ? countryOptions : await loadCountryOptions();
      const country = countries.find(c => c.code === loaded.country_code) || countries.find(c => c.label === loaded.country_name);
      if (country) {
        loaded.country_id = country.value;
        const cities = await loadCityOptions(country.value);
        const city = cities.find(c => c.code === loaded.city_code && c.label === loaded.city_name) || cities.find(c => c.label === loaded.city_name);
        if (city) loaded.city_id = city.value;
      } else {
        setCityOptions([]);
      }
      setForm(loaded);
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
      setModal(m => ({ ...m, show: false }));
    } finally {
      setModalLoading(false);
    }
  };

  const handleCountryChange = (o) => {
    setForm({ ...form, country_id: o ? o.value : "", country_name: o ? o.label : "", country_code: o ? o.code : "", city_id: "", city_name: "", city_code: "" });
    loadCityOptions(o ? o.value : "");
  };

  const handleCityChange = (o) => setForm({ ...form, city_id: o ? o.value : "", city_name: o ? o.label : "", city_code: o ? o.code : "" });

  const handleFormText = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  /* ---------- API: create / update (with confirmation popup) ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.airport_name ?? "").trim() || form.airport_name === 0) { toast.error("Airport Name is required"); return; }
    if (!String(form.airport_code ?? "").trim() || form.airport_code === 0) { toast.error("Airport Code is required"); return; }
    if (!String(form.country_name ?? "").trim()) { toast.error("Country is required"); return; }
    if (!String(form.city_name ?? "").trim()) { toast.error("City is required"); return; }

    const confirm = await MySwal.fire({
      title: isEdit ? "Update Airport?" : "Save Airport?",
      text: isEdit ? "Do you want to update this airport?" : "Do you want to add this airport?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);
      const payload = formToPayload(form);
      const res = isEdit
        ? await airportsService.update(modal.id, payload)
        : await airportsService.create(payload);
      if (res?.success) {
        toast.success(res.message);
        closeModal();
        loadItems();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  /* ---------- API: status change (with confirmation) ---------- */
  const handleStatusChange = async (row, newStatus) => {
    if (newStatus === row?.status) return;
    const result = await MySwal.fire({
      title: "Change status?",
      text: "Are you sure you want to mark this airport as " + newStatus + "?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await airportsService.update(row?.id, { ...formToPayload(rowToForm(row)), status: newStatus });
      if (res?.success) toast.success(res.message); else toast.error(res?.message);
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
      loadItems();
    }
  };

  /* ---------- API: delete (with confirmation) ---------- */
  const handleDelete = async (row) => {
    const result = await MySwal.fire({
      title: "Delete Airport?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await airportsService.delete(row?.id);
      if (res?.success) toast.success(res.message); else toast.error(res?.message);
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
      loadItems();
    }
  };

  /* ---------- export ---------- */
  const csvHeaders = [
    { label: "Airport", key: "c0" },
    { label: "Code", key: "c1" },
    { label: "IATA", key: "c2" },
    { label: "ICAO", key: "c3" },
    { label: "City", key: "c4" },
    { label: "Country", key: "c5" },
    { label: "Status", key: "status" },
  ];
  const csvData = items.map(row => ({
    c0: row?.airport_name,
    c1: row?.airport_code,
    c2: row?.iata_id,
    c3: row?.icao_id,
    c4: row?.city_name,
    c5: row?.country_name,
    status: row?.status,
  }));

  return (
    <>
      <section className="section dashboard">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Airports</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/setting" style={{ color: "#475569" }}>Setting</Link>              
                            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>Airports</span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button type="button" onClick={openAdd} className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
              <i className="bi bi-plus-lg"></i> Add Airport
            </button>
            <CSVLink data={csvData} headers={csvHeaders} filename="airports.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        {/* Filters */}
        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input type="text" className="form-control" name="search" placeholder="Search airport name, code or city"
                  value={filters.search} onChange={handleFilterText} style={filterInputStyle} />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={STATUS_FILTER_OPTIONS.find(o => o.value === filters.status) || null}
                  onChange={(o) => setFilters({ ...filters, status: o ? o.value : "" })}
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button type="submit" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                  <i className="bi bi-search"></i> Apply
                </button>
                <button type="button" onClick={handleReset} className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Stat chips */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{totalRecords}</b> Total Airports</span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{activeCount}</b> Active on this page</span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{items.length - activeCount}</b> Inactive on this page</span>
        </div>

        {/* Table */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(255,255,255,0.6)', display: 'flex',
                justifyContent: 'center', alignItems: 'center', zIndex: 10
              }}>
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", "Airport","Code","IATA","ICAO","City","Country","Status","Actions"].map((h, i) => (
                      <th key={i} style={{
                        fontSize: "11px", fontWeight: "700", color: "#475569",
                        textTransform: "uppercase", letterSpacing: "0.6px",
                        borderBottom: "2px solid #e8edf5", padding: "10px 12px",
                        whiteSpace: "nowrap", background: "#f8f9fe"
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, sno_i) => (
                    <tr key={row?.id}>
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                      <td style={{ fontSize: 13 }}>{row?.airport_name ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.airport_code ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.iata_id ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.icao_id ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.city_name ?? "-"}</td>
                      <td style={{ fontSize: 13 }}>{row?.country_name ?? "-"}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row?.status}
                          onChange={(e) => handleStatusChange(row, e.target.value)}
                          style={{
                            width: 'auto', minWidth: 0, height: 28, padding: '0 26px 0 10px', backgroundPosition: 'right 8px center',
                            borderRadius: 8, fontSize: 12, fontWeight: 600,
                            backgroundColor: row?.status === "active" ? '#d4edda' : '#f8d7da',
                            color: row?.status === "active" ? '#155724' : '#721c24',
                            border: 'none'
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          {row?.status !== "active" && row?.status !== "inactive" && <option value={row?.status}>{row?.status}</option>}
                        </select>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button className="btn btn-sm d-flex align-items-center justify-content-center" title="View"
                            onClick={() => openExisting(row, "view")} style={iconBtn('#e6f7f1', '#0f8a5f')}>
                            <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                          </button>
                          <button className="btn btn-sm d-flex align-items-center justify-content-center" title="Edit"
                            onClick={() => openExisting(row, "edit")} style={iconBtn('#e8f0fe', 'var(--btn-bg)')}>
                            <i className={`bi ${ACTION_ICONS.edit}`} data-action="edit" style={{ fontSize: 13 }}></i>
                          </button>
                          <button className="btn btn-sm d-flex align-items-center justify-content-center" title="Delete"
                            onClick={() => handleDelete(row)} style={iconBtn('#fce8e6', '#d93025')}>
                            <i className={`bi ${ACTION_ICONS.delete}`} data-action="delete" style={{ fontSize: 13 }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && items.length === 0 && (
                    <tr><td colSpan={9} className="text-center text-muted py-4">No records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
            <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> airports</div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select className="form-select form-select-sm" style={{ width: 100, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
              </select>
              <span>Go</span>
              <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
                value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
              <span>/ {totalPage}</span>
              <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
              <div className="d-flex align-items-center gap-1">
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
                <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
                {getPageNumbers().map((n, i) => n === "..."
                  ? <span key={"e" + i} className="px-1">...</span>
                  : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
                <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
              </div>
            </div>
          </div>
        </div>

        {/* Add / Update / View popup */}
        <Modal show={modal.show} onHide={closeModal} centered scrollable size="lg">
          <Modal.Header closeButton closeVariant={isView ? "white" : undefined} style={isView ? { background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)", borderBottom: "none" } : undefined}>
            <div>
              <Modal.Title className="d-flex align-items-center gap-2" style={isView ? { color: "#fff", fontSize: 18, fontWeight: 600 } : { fontSize: 16, fontWeight: 700 }}>
                {isView && <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i>}
                {isView ? "Airport Details" : isEdit ? "Update Airport" : "Add Airport"}
              </Modal.Title>
              {!isView && <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)", marginTop: 2 }}>
                {isView ? "View airport information" : isEdit ? "Update airport information" : "Add a new airport to the system"}
              </div>}
            </div>
          </Modal.Header>
          <form onSubmit={handleSubmit}>
            <Modal.Body style={isView ? { padding: 24, background: "#f8f9fe" } : undefined}>
              {modalLoading ? <ContentLoader loaderType="circleSpin" /> : isView ? (
                <DetailView fields={[
                  { label: "Airport Name", value: form.airport_name },
                  { label: "Airport Code", value: form.airport_code },
                  { label: "IATA ID", value: form.iata_id },
                  { label: "ICAO ID", value: form.icao_id },
                  { label: "World Area Code", value: form.world_area_code },
                  { label: "Country", value: form.country_name },
                  { label: "Country Code", value: form.country_code },
                  { label: "City", value: form.city_name },
                  { label: "City Code", value: form.city_code },
                  { label: "Latitude", value: form.latitude },
                  { label: "Longitude", value: form.longitude },
                  { label: "Status", value: form.status, badge: true },
                ]} />
              ) : (
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>Airport Name <span className="text-danger">*</span></div>
                    <input type="text" className="form-control" name="airport_name" placeholder="e.g. Indira Gandhi International"
                      value={form.airport_name} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>Airport Code <span className="text-danger">*</span></div>
                    <input type="text" className="form-control" name="airport_code" placeholder="e.g. DEL" maxLength={5}
                      value={form.airport_code} onChange={(e) => setForm({ ...form, airport_code: e.target.value.toUpperCase() })} style={inputStyle} />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>IATA ID</div>
                    <input type="text" className="form-control" name="iata_id" placeholder="e.g. DEL" maxLength={3}
                      value={form.iata_id} onChange={(e) => setForm({ ...form, iata_id: e.target.value.toUpperCase() })} style={inputStyle} />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>ICAO ID</div>
                    <input type="text" className="form-control" name="icao_id" placeholder="e.g. VIDP" maxLength={4}
                      value={form.icao_id} onChange={(e) => setForm({ ...form, icao_id: e.target.value.toUpperCase() })} style={inputStyle} />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>World Area Code</div>
                    <input type="text" className="form-control" name="world_area_code" placeholder="e.g. 321"
                      value={form.world_area_code} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>Status</div>
                    <Select
                      options={STATUS_OPTIONS}
                      value={STATUS_OPTIONS.find(o => o.value === form.status) || null}
                      onChange={(o) => setForm({ ...form, status: o ? o.value : "active" })}
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>Country <span className="text-danger">*</span></div>
                    <Select
                      options={countryOptions}
                      value={countryOptions.find(o => o.value === form.country_id) || null}
                      onChange={handleCountryChange}
                      isClearable
                      placeholder="Select Country"
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>Country Code</div>
                    <input type="text" className="form-control" placeholder="Auto-filled" readOnly
                      value={form.country_code} style={{ ...inputStyle, background: "#f4f5f8" }} />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>City <span className="text-danger">*</span></div>
                    <Select
                      options={cityOptions}
                      value={cityOptions.find(o => o.value === form.city_id) || null}
                      onChange={handleCityChange}
                      isClearable
                      isDisabled={!form.country_id}
                      placeholder="Select City"
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>City Code</div>
                    <input type="text" className="form-control" placeholder="Auto-filled" readOnly
                      value={form.city_code} style={{ ...inputStyle, background: "#f4f5f8" }} />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>Latitude</div>
                    <input type="number" className="form-control" name="latitude" placeholder="e.g. 28.5562" step="any"
                      value={form.latitude} onChange={handleFormText} style={inputStyle} />
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={labelStyle}>Longitude</div>
                    <input type="number" className="form-control" name="longitude" placeholder="e.g. 77.1000" step="any"
                      value={form.longitude} onChange={handleFormText} style={inputStyle} />
                  </div>
                </div>
              )}
            </Modal.Body>
            {!isView && <Modal.Footer className="justify-content-end gap-2">
              <button type="button" onClick={closeModal} className="btn btn-sm px-4"
                style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 6, height: 34, fontSize: 13, color: "#444" }}>
                {isView ? "Close" : "Cancel"}
              </button>
              {!isView && (
                <button type="submit" disabled={saving || modalLoading} className="btn btn-sm px-4 text-white fw-semibold"
                  style={{ background: "var(--btn-bg)", border: "none", borderRadius: 6, height: 34, fontSize: 13 }}>
                  {isEdit ? "Update Airport" : "Add Airport"}
                </button>
              )}
            </Modal.Footer>}
          </form>
        </Modal>
      </section>
    </>
  )
}

export default AirportList;
